/**
 * Report Type → ambient TS (ADR-0060 S2) — the `Output` interface generated
 * from the Type's schema tree, plus the minimal `TrayReportCtx` the code
 * provider's handler sees. Injected into Monaco (`CodeField.ambientDts`) so a
 * `function handler(ctx: TrayReportCtx, input: Input): Output[]` body is
 * SEMANTICALLY checked against the contract — the DevExpress schema-first
 * promise, enforced by the type-checker.
 */
import type { ReportTypeNode } from "@/lib/api-reports";

const TS_SCALAR: Record<string, string> = {
  text: "string",
  integer: "number",
  decimal: "number",
  date: "string",
  datetime: "string",
  bool: "boolean",
  image: "string",
};

function pascal(id: string): string {
  return id
    .split("_")
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join("");
}

/** Emit `interface Output {...}` (+ one nested interface per container node,
 *  named after the node id path) from the Type tree. Every field is optional —
 *  the contract is lenient on absence (mirrors backend `validate_instances`). */
export function reportOutputDts(nodes: ReportTypeNode[]): string {
  const blocks: string[] = [];

  const emit = (items: ReportTypeNode[], name: string): string => {
    const fields = items.map((n) => {
      if (n.type === "object") {
        const child = emit(n.children ?? [], `${name}${pascal(n.id)}`);
        return `  ${n.id}?: ${child} | null;`;
      }
      if (n.type === "collection") {
        const child = emit(n.children ?? [], `${name}${pascal(n.id)}`);
        return `  ${n.id}?: ${child}[];`;
      }
      return `  ${n.id}?: ${TS_SCALAR[n.type] ?? "unknown"} | null;`;
    });
    blocks.push(`interface ${name} {\n${fields.join("\n")}\n}`);
    return name;
  };

  emit(nodes, "Output");
  return blocks.join("\n\n");
}

/** The read-op union — MIRROR of the apps-plane `_OPS_DTS` (AppQueriesPane):
 *  the report handler funnels into the SAME `_build_where`, so the grammar
 *  must not drift. */
const OPS_DTS = `"eq" | "ne" | "lt" | "lte" | "gt" | "gte" | "in" | "contains" | "is_null" | "not_null" | "has"`;

/** System stamps every table also carries — mirror of AppQueriesPane `_SYS_COLS`. */
const SYS_COLS = ["id", "created_at", "updated_at"];

/** A cross-app table the report handler can read: the namespaced ref +
 *  its data-column names (used to type `column:` and `rows[0].`). */
export interface ReportTableRef {
  ref: string;
  columns: string[];
}

/** Which contract interfaces the ambient should SUPPLY. The user code is a
 *  global script, so an `interface Output`/`Input` DECLARED IN THE BUFFER
 *  merges with the ambient one — and the generated members (`titolo?: string |
 *  null`) conflict with a hand-written `titolo: string` (TS2717), red-flagging
 *  a perfectly good handler and BLOCKING the save (`tsErrors > 0`). So the
 *  editor omits an interface the buffer already declares: the user's own
 *  declaration wins (the real contract gate stays the server-side
 *  `validate_instances`), the ambient covers the skeleton case. */
export interface ReportDtsOptions {
  includeOutput?: boolean;
  includeInput?: boolean;
}

/** The full ambient DTS for the code-provider editor: Output + the handler ctx.
 *  `tables` are the CROSS-APP `"slug/tabella"` refs (every app in the tenant —
 *  a Report Type has no app binding) WITH their columns; the refs become a
 *  string-literal union so `ctx.db.query('` autocompletes, and — mirroring the
 *  apps-plane `buildLogicDts` — each ref gets a column union + a typed ROW so
 *  `column: '` and `rows[0].` autocomplete the real fields. */
export function reportProviderDts(
  nodes: ReportTypeNode[],
  tables: ReportTableRef[] = [],
  opts: ReportDtsOptions = {},
): string {
  const { includeOutput = true, includeInput = true } = opts;
  const colUnion = (cols: string[]): string => {
    if (!cols.length) return "string"; // unknown columns → stay permissive
    const all = Array.from(new Set([...cols, ...SYS_COLS]));
    return all.map((c) => JSON.stringify(c)).join(" | ");
  };
  const rowName = (t: ReportTableRef) => `Row_${t.ref.replace(/[^A-Za-z0-9_]/g, "_")}`;
  const rowIface = (t: ReportTableRef): string => {
    const cols = Array.from(new Set([...t.columns, ...SYS_COLS]));
    const fields = cols.map((c) => `  ${JSON.stringify(c)}: any;`).join("\n");
    return `interface ${rowName(t)} {\n${fields}\n}`;
  };

  // No tables known yet → a simple non-generic shape (ref/column = string).
  // With tables → per-ref column unions + typed rows, exactly like buildLogicDts.
  const tableMap = tables.length
    ? [
        `interface TableColumns {\n${tables
          .map((t) => `  ${JSON.stringify(t.ref)}: ${colUnion(t.columns)};`)
          .join("\n")}\n}\ntype TrayTableName = keyof TableColumns;`,
        tables.map(rowIface).join("\n"),
        `type RowOf<T extends TrayTableName> =\n${tables
          .map((t) => `  T extends ${JSON.stringify(t.ref)} ? ${rowName(t)} :`)
          .join("\n")}\n  Record<string, any>;`,
      ].join("\n")
    : `type TrayTableName = string;\ntype TableColumns = Record<string, string>;\ntype RowOf<T extends TrayTableName = TrayTableName> = Record<string, any>;`;

  const inputBlock = includeInput
    ? `/** The report parameters the caller supplies (e.g. the order id). */
interface Input {
  [key: string]: any;
}`
    : "";

  return `${includeOutput ? reportOutputDts(nodes) : ""}

/* Cross-app table refs — ALWAYS "app_slug/tabella" (pick one in ctx.db.query). */
${tableMap}
type ColsOf<T extends TrayTableName> = Extract<TableColumns[T], string>;

${inputBlock}

/** Il valore di un filtro: uno scalare (o un array per 'in'/'has'). */
type TrayFilterValue = string | number | boolean | null | (string | number | boolean)[];
/** Un filtro WHERE: { column, op, value }. op 'in' vuole un array; 'is_null' /
 *  'not_null' ignorano value; 'has' = id in un array relation_many. */
interface TrayFilter<C extends string = string> { column: C; op: ${OPS_DTS}; value?: TrayFilterValue; }
/** Un nodo dell'albero booleano (ADR-0055): una FOGLIA { column, op, value } oppure un
 *  connettivo { and: [...] } / { or: [...] } / { not: ... }. Una lista di filtri = AND. */
type TrayFilterNode<C extends string = string> =
  | TrayFilter<C>
  | { and: TrayFilterNode<C>[] }
  | { or: TrayFilterNode<C>[] }
  | { not: TrayFilterNode<C> };
interface TraySort<C extends string = string> { column: C; descending?: boolean; }
/** Opzioni di lettura. \`filters\` = una LISTA di nodi (AND implicito) OPPURE un singolo
 *  nodo/albero \`{and/or/not}\`; \`filter\` = alias per un singolo albero. */
interface TrayReportQueryOpts<C extends string = string> {
  filters?: TrayFilterNode<C>[] | TrayFilterNode<C>;
  filter?: TrayFilterNode<C>;
  sort?: TraySort<C>[];
  search?: string;
  page?: number;
  page_size?: number;
}

interface TrayReportQueryResult<T extends TrayTableName = TrayTableName> {
  rows: RowOf<T>[];
  total: number;
}

/** Read-only data reach of the report handler — every app, slug-namespaced. */
interface TrayReportCtx {
  db: {
    /** Legge le righe di una tabella come "app_slug/tabella". DUE forme:
     *  • STRUTTURATA — \`ctx.db.query('crm/deals', { filters: [{ column: 'stage',
     *    op: 'eq', value: input.s }], sort: [{ column: 'amount', descending: true }] })\`;
     *    \`column\` autocompleta le colonne reali; \`filters\` accetta anche
     *    { and/or/not } (albero booleano). \`rows[0].\` autocompleta le colonne.
     *  • PREDICATO — \`ctx.db.query('crm/deals', d => d.stage === 'won')\`: la freccia
     *    NON viene eseguita, al salvataggio è tradotta in un filtro.
     *  Una sola firma con parametro a UNIONE (mai overload separati). Read-only. */
    query<T extends TrayTableName>(
      table: T,
      optsOrPredicate?: TrayReportQueryOpts<ColsOf<T>> | ((row: RowOf<T>) => boolean),
    ): TrayReportQueryResult<T>;
  };
  /** The report parameters. */
  input: Input;
  /** Abort with an explicit error message. */
  fail(message: string): never;
}
`;
}

/** New-provider starter — the `handler` shape `compileAppLogic` bridges. */
export const REPORT_HANDLER_SKELETON = `function handler(ctx: TrayReportCtx, input: Input): Output[] {
  // Leggi i dati con ctx.db.query('app_slug/tabella') e ritorna SEMPRE una lista di Output.
  return [];
}
`;
